import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api.js";

const DOC_TITLE = { w9: "Form W-9 — Request for Taxpayer Identification Number", nda: "Mutual Non-Disclosure Agreement" };

// Print-style view of a signed contractor document — same pattern as the
// invoice print page: toolbar hides on print, browser handles PDF/share.
export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api(`/api/documents/${id}`)
      .then((d) => setDoc(d.document))
      .catch(() => setErr("Document not found."));
  }, [id]);

  if (err) return <p className="p-8 text-sm text-red-600">{err}</p>;
  if (!doc) return <p className="p-8 text-center text-sm text-slate-400">Loading…</p>;

  const f = doc.fields || {};
  const rows =
    doc.doc_type === "w9"
      ? [
          ["Legal name", f.name],
          ["Business name", f.business || "—"],
          ["Tax classification", f.classification],
          ["Address", [f.address, f.city, f.statezip].filter(Boolean).join(", ")],
          ["Taxpayer ID", doc.tin_last4 ? `···-··-${doc.tin_last4} (encrypted on file)` : "—"],
        ]
      : [];

  return (
    <div className="min-h-full bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-3 print:hidden">
        <button onClick={() => navigate(-1)} className="text-sm font-semibold text-navy-700">
          ‹ Back
        </button>
        <button onClick={() => window.print()} className="rounded-xl bg-navy-700 px-5 py-2 text-sm font-semibold text-white">
          🖨️ Print / Save PDF
        </button>
      </div>

      <div className="mx-auto max-w-2xl px-8 py-10">
        <p className="text-2xl font-bold" style={{ color: "#234E70" }}>
          AI Engines
        </p>
        <p className="mt-1 text-xs text-slate-500">Marion, Indiana · aiengines.work</p>

        <h1 className="mt-8 text-lg font-semibold text-slate-800">{DOC_TITLE[doc.doc_type] || "Document"}</h1>
        <p className="text-sm text-slate-500">
          {doc.contact_company || doc.contact_name}
          {doc.contact_company && doc.contact_name ? ` — ${doc.contact_name}` : ""}
        </p>

        {rows.length > 0 && (
          <table className="mt-6 w-full text-sm">
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label} className="border-b border-slate-100">
                  <td className="w-44 py-2.5 pr-4 align-top text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</td>
                  <td className="py-2.5 text-slate-800">{value || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {doc.doc_type === "nda" && (
          <p className="mt-4 text-sm text-slate-600">
            The full agreement text as presented at signing is retained with this record; signature below binds both
            parties per its terms.
          </p>
        )}

        <div className="mt-8 rounded-xl px-4 py-3" style={{ backgroundColor: "#234E7010" }}>
          <p className="text-sm">
            <span className="font-semibold text-slate-800">Signed:</span>{" "}
            <span className="font-serif text-lg italic text-slate-900">{doc.signature_name}</span>
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {doc.signed_at} UTC · electronic signature with recorded consent (ESIGN / Indiana UETA)
          </p>
        </div>

        <div className="mt-6 text-[11px] leading-relaxed text-slate-400">
          <p className="font-semibold uppercase tracking-wide">Audit trail</p>
          <p>Sent {doc.sent_at} · first opened {doc.viewed_at || "—"} · signed {doc.signed_at}</p>
          <p>Signer IP {doc.signer_ip || "—"}</p>
          <p className="break-all">Record integrity hash (SHA-256): {doc.record_hash}</p>
        </div>
      </div>
    </div>
  );
}
